import { ChartDataset } from 'chart.js';
import { ref } from 'valtio';
import { ColorsPallete } from '../../common/common-utils';
import { ScanHistory } from '../scan-history-types';
import ScanHistoryStore from '../stores/scan-history-store';

export const formatScanHistoryData = (data: ScanHistory[]) => {
  const sortedData = [...data].sort((a, b) =>
    a.scanDate > b.scanDate ? 1 : -1,
  );
  const labels = sortedData.map(d => ({
    scanDate: d.scanDate,
    scanId: d.scanId,
  }));
  const categories = new Set();
  const timeout = {};
  const execution = {};
  const finalizing = {};
  const errors = {};
  const totalTime = {};

  sortedData.forEach(
    ({
      policyPerCatStats,
      scanStartDate,
      scanType,
      scanFinishDate,
      scanInfoStats: {
        totalFinalizingTime,
        blameCodeAtFinalizing,
        blameArtifactsAtFinalizing,
        dockerhubTimeAtFinalizing,
        supplyChainTimeAtFinalizing,

        repoScanTime,
        audiLogTime,
        artifactTrivyExecution,
        artifactAndParserExecution,
        cicdScanTime,
        wikiProcessTime,
        totalCloudScanTime,
        prowlerTime,
        oxCrawlerTime,

        dockerhubNumTimeouts,
        failedAlertRecommendationTimeout,
        failedAutoFixTimeout,
        failedBlameTimeout,
        failedDependencyGraphTimeout,
        failedIacValidatorTimeout,
        failedOpenWikiTimeout,
        failedScaValidatorTimeout,
        failedSecretTimeout,
        timeoutClones,

        dockerhubNumFails,
        failedAlertRecommendationBatches,
        failedAutoFixBatches,
        failedBlameBatches,
        failedCalcGraph,
        failedClones,
        failedDependencyGraphBatches,
        failedFindConnectionOpenWiki,
        failedFromOpenWiki,
        failedIacValidatorBatches,
        failedOpenWikiBatches,
        failedProcessSingleCICD,
        failedProcessSingleCloud,
        failedProcessSingleRepo,
        failedScaValidatorBatches,
        failedSecretBatches,
        failedSetNewSeverity,
        mongoErrors,
      },
    }) => {
      policyPerCatStats.map(({ category }) => categories.add(category));
      const key = scanType || 'no scan type data';
      try {
        if (!totalTime[key]) {
          totalTime[key] = [];
        }
        Object.keys(totalTime).forEach(k => {
          if (k === key) {
            totalTime[key].push(
              (new Date(scanFinishDate).getTime() -
                new Date(scanStartDate).getTime()) /
                (60 * 1000),
            );
          } else {
            totalTime[k].push(null);
          }
        });
      } catch {
        totalTime[key].push(null);
      }

      fillObject(
        finalizing,
        'totalFinalizingTime',
        totalFinalizingTime || 0,
        true,
      );
      fillObject(
        finalizing,
        'blameCodeAtFinalizing',
        blameCodeAtFinalizing || 0,
        true,
      );
      fillObject(
        finalizing,
        'blameArtifactsAtFinalizing',
        blameArtifactsAtFinalizing || 0,
        true,
      );
      fillObject(
        finalizing,
        'dockerhubTimeAtFinalizing',
        dockerhubTimeAtFinalizing || 0,
        true,
      );
      fillObject(
        finalizing,
        'supplyChainTimeAtFinalizing',
        supplyChainTimeAtFinalizing || 0,
        true,
      );
      fillObject(finalizing, 'wikiProcessTime', wikiProcessTime || 0, true);

      fillObject(execution, 'repoScanTime', repoScanTime || 0, true);
      fillObject(execution, 'audiLogTime', audiLogTime || 0, true);
      fillObject(
        execution,
        'artifactTrivyExecution',
        artifactTrivyExecution || 0,
        true,
      );
      fillObject(
        execution,
        'artifactAndParserExecution',
        artifactAndParserExecution || 0,
        true,
      );
      fillObject(execution, 'cicdScanTime', cicdScanTime || 0, true);

      fillObject(
        execution,
        'totalCloudScanTime',
        totalCloudScanTime || 0,
        true,
      );
      fillObject(execution, 'prowlerTime', prowlerTime || 0, true);
      fillObject(execution, 'oxCrawlerTime', oxCrawlerTime || 0, true);

      fillObject(timeout, 'dockerhubNumTimeouts', dockerhubNumTimeouts || 0);
      fillObject(
        timeout,
        'failedAlertRecommendationTimeout',
        failedAlertRecommendationTimeout || 0,
      );
      fillObject(timeout, 'failedAutoFixTimeout', failedAutoFixTimeout || 0);
      fillObject(timeout, 'failedBlameTimeout', failedBlameTimeout || 0);
      fillObject(
        timeout,
        'failedDependencyGraphTimeout',
        failedDependencyGraphTimeout || 0,
      );
      fillObject(
        timeout,
        'failedIacValidatorTimeout',
        failedIacValidatorTimeout || 0,
      );
      fillObject(timeout, 'failedOpenWikiTimeout', failedOpenWikiTimeout || 0);
      fillObject(
        timeout,
        'failedScaValidatorTimeout',
        failedScaValidatorTimeout || 0,
      );
      fillObject(timeout, 'failedSecretTimeout', failedSecretTimeout || 0);
      fillObject(timeout, 'failedBlameTimeout', failedBlameTimeout || 0);
      fillObject(timeout, 'timeoutClones', timeoutClones || 0);

      fillObject(errors, 'dockerhubNumFails', dockerhubNumFails || 0);
      fillObject(
        errors,
        'failedAlertRecommendationBatches',
        failedAlertRecommendationBatches || 0,
      );
      fillObject(errors, 'failedAutoFixBatches', failedAutoFixBatches || 0);
      fillObject(errors, 'failedBlameBatches', failedBlameBatches || 0);
      fillObject(errors, 'failedCalcGraph', failedCalcGraph || 0);
      fillObject(errors, 'failedClones', failedClones || 0);
      fillObject(
        errors,
        'failedDependencyGraphBatches',
        failedDependencyGraphBatches || 0,
      );
      fillObject(
        errors,
        'failedFindConnectionOpenWiki',
        failedFindConnectionOpenWiki || 0,
      );
      fillObject(errors, 'failedFromOpenWiki', failedFromOpenWiki || 0);
      fillObject(
        errors,
        'failedIacValidatorBatches',
        failedIacValidatorBatches || 0,
      );
      fillObject(errors, 'failedOpenWikiBatches', failedOpenWikiBatches || 0);

      fillObject(
        errors,
        'failedProcessSingleCICD',
        failedProcessSingleCICD || 0,
      );
      fillObject(
        errors,
        'failedProcessSingleCloud',
        failedProcessSingleCloud || 0,
      );
      fillObject(
        errors,
        'failedProcessSingleRepo',
        failedProcessSingleRepo || 0,
      );
      fillObject(
        errors,
        'failedScaValidatorBatches',
        failedScaValidatorBatches || 0,
      );
      fillObject(errors, 'failedSecretBatches', failedSecretBatches || 0);
      fillObject(errors, 'failedSetNewSeverity', failedSetNewSeverity || 0);
      fillObject(errors, 'mongoErrors', mongoErrors || 0);
    },
  );

  ScanHistoryStore.categoryBreakdownData = ref(
    Array.from(categories).map((category, i) => ({
      label: category,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
      data: sortedData.map(
        ({ policyPerCatStats }) =>
          policyPerCatStats.find(c => c.category === category)?.count || 0,
      ),
    })),
  ) as ChartDataset<'line', number[]>[];

  ScanHistoryStore.labels = labels;
  ScanHistoryStore.timeExecutionData = ref(
    Object.entries(execution).map(([key, value], i) => ({
      label: key.split(/(?=[A-Z])/).join(' '),
      data: value,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
    })),
  ) as ChartDataset<'line', number[]>[];
  ScanHistoryStore.timeFinalizingData = ref(
    Object.entries(finalizing).map(([key, value], i) => ({
      label: key.split(/(?=[A-Z])/).join(' '),
      data: value,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
    })),
  ) as ChartDataset<'line', number[]>[];
  ScanHistoryStore.timeoutsData = ref(
    Object.entries(timeout).map(([key, value], i) => ({
      label: key.split(/(?=[A-Z])/).join(' '),
      data: value,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
    })),
  ) as ChartDataset<'line', number[]>[];

  ScanHistoryStore.errorsData = ref(
    Object.entries(errors).map(([key, value], i) => ({
      label: key.split(/(?=[A-Z])/).join(' '),
      data: value,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
    })),
  ) as ChartDataset<'line', number[]>[];

  ScanHistoryStore.totalTime = ref(
    Object.entries(totalTime).map(([key, value], i) => ({
      label: key.split(/(?=[A-Z])/).join(' '),
      data: value,
      borderColor: ColorsPallete[i],
      backgroundColor: ColorsPallete[i],
    })),
  ) as ChartDataset<'line', number[]>[];
};

const fillObject = (object, key, value, time = false) => {
  if (!object[key]) {
    object[key] = [];
  }
  object[key].push(time ? +value / (1000 * 60) : value);
};
